import { Question } from "entities";
import { DateTime } from "luxon";
import React, { useEffect, useReducer, useState } from "react";

import {
  Button,
  Container,
  Dimmer,
  DimmerDimmable,
  Dropdown,
  Form,
  FormGroup,
  FormTextArea,
  Header,
  HeaderSubheader,
  Icon,
  Loader,
  Message,
  MessageHeader,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  Segment,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "semantic-ui-react";
import { AppService, createDto, updateDto } from "services";
import { Link } from "react-router-dom";
import { Formik } from "formik";
import * as Yup from "yup";
import { DateInput } from "semantic-ui-calendar-react";

// Fonction utilitaire pour obtenir la date d'aujourd'hui de manière sécurisée
function getTodayDate(): Date {
  try {
    // Utiliser la date du navigateur (client)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Vérification de validité
    if (isNaN(today.getTime())) {
      throw new Error("Date invalide calculée");
    }
    
    return today;
  } catch (error) {
    // Fallback en cas d'erreur : date fixe
    const fallbackToday = new Date();
    fallbackToday.setHours(0, 0, 0, 0);
    
    console.warn("Fallback utilisé pour le calcul de date:", error);
    return fallbackToday;
  }
}

function modalReducer(state, action) {
  switch (action.type) {
    case "OPEN_MODAL":
      return { open: true, dimmer: action.dimmer, isEdit: action.isEdit };
    case "CLOSE_MODAL":
      return { open: false };
    default:
      throw new Error();
  }
}

const QuestionsPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [name, setName] = useState("");

  const [devId, setDevId] = useState("");
  const [devices, setDevices] = useState<any[]>([]);

  const [errorDelete, setErrorDelete] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [data, setData] = useState<updateDto>({
    question_id: "",
    dev_id: "",
    question: "",
    date_debut: "",
    date_fin: "",
  });
  const columns = [
    "dev_id",
    "question",
    "date_debut",
    "date_fin",
    "date_mise_a_jour",
    "data_count",
  ];

  const [state, dispatch] = useReducer(modalReducer, {
    open: false,
    dimmer: "blurring",
    isEdit: false,
  });
  const { open, dimmer, isEdit } = state;

  const onCreate = () => {
    setData({
      question_id: "",
      dev_id: "",
      question: "",
      date_debut: "",
      date_fin: "",
    });
    dispatch({ type: "OPEN_MODAL", isEdit: false });
  };

  const onEdit = (item: Question) => {
    setData({
      ...item,
      date_debut: DateTime.fromISO(item.date_debut).toFormat("y-MM-dd"),
      date_fin: DateTime.fromISO(item.date_fin).toFormat("y-MM-dd"),
    });
    dispatch({ type: "OPEN_MODAL", isEdit: true });
  };

  const onDuplicate = (item: Question) => {
    setData({
      question_id: "",
      dev_id: item.dev_id,
      question: item.question,
      date_debut: "",
      date_fin: "",
    });
    dispatch({ type: "OPEN_MODAL", isEdit: false });
  };

  const onDelete = (item: Question) => {
    setData({
      ...item,
      date_debut: DateTime.fromISO(item.date_debut).toFormat("y-MM-dd"),
      date_fin: DateTime.fromISO(item.date_fin).toFormat("y-MM-dd"),
    });
    setDeleteModalOpen(true);
    setErrorDelete(false);
  };

  const onClose = () => {
    setData({
      question_id: "",
      dev_id: "",
      question: "",
      date_debut: "",
      date_fin: "",
    });
    dispatch({ type: "CLOSE_MODAL" });
    setDeleteModalOpen(false);
    setError(false);
    setErrorDelete(false);
  };

  const submitDelete = async () => {
    setErrorDelete(false);
    setLoading(true);

    try {
      const res = await AppService.deleteQuestion({
        question_id: data.question_id,
        dev_id: data.dev_id,
      });

      if (res.success) {
        setQuestions(
          questions.filter((i) => i.question_id !== data.question_id)
        );
        onClose();
      } else {
        setErrorDelete(true);
      }
      setLoading(false);
    } catch (error) {
      setErrorDelete(true);
      setLoading(false);
    }
  };

  const submit = async (body) => {
    setError(false);

    if (isEdit) {
      const res = await AppService.updateQuestion({
        ...body,
        question_id: data.question_id,
        date_debut: DateTime.fromFormat(body.date_debut, "y-MM-dd")
          .startOf("day")
          .setZone("Pacific/Tahiti")
          .toISO(),
        date_fin: DateTime.fromFormat(body.date_fin, "y-MM-dd")
          .endOf("day")
          .plus({ hour: -1 })
          .setZone("Pacific/Tahiti")
          .toISO(),
      } as updateDto);

      if (res) {
        setQuestions(
          questions.map((i) => (i.question_id === res.question_id ? res : i))
        );
        onClose();
      } else {
        setError(true);
      }
    } else {
      const res = await AppService.createQuestion({
        ...body,
        date_debut: DateTime.fromFormat(body.date_debut, "y-MM-dd")
          .startOf("day")
          .setZone("Pacific/Tahiti")
          .toISO(),
        date_fin: DateTime.fromFormat(body.date_fin, "y-MM-dd")
          .endOf("day")
          .plus({ hour: -1 })
          .setZone("Pacific/Tahiti")
          .toISO(),
      } as createDto);
      if (res) {
        setQuestions([res, ...questions]);
        onClose();
      } else {
        setError(true);
      }
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [q, devIds] = await Promise.all([
        AppService.listQuestion(),
        AppService.listDevice(),
      ]);
      setLoading(false);
      if (!q || !devIds) setError(true);
      setName(q?.name ?? "");
      setDevices(
        devIds.map((e) => ({
          key: e.dev_id,
          value: e.dev_id,
          text: e.dev_id,
        }))
      );
      setQuestions(
        q.questions.sort(
          (a, b) =>
            DateTime.fromISO(b.date_debut).valueOf() -
            DateTime.fromISO(a.date_debut).valueOf()
        )
      );
    })();
  }, []);

  return (
    <>
      <Container>
        <Container clearing>
          <Container style={{ marginBottom: "2em" }}>
            <Header as="h3" attached="top" textAlign="center">
              {name ?? ""}
            </Header>
          </Container>

          <Header as="h3" floated="left">
            Questions
            <HeaderSubheader>Listes des questions créées</HeaderSubheader>
          </Header>
          <Container as="div" floated="right" textAlign="right">
            <Dropdown
              placeholder="Filtrer par borne"
              options={devices}
              selection
              clearable
              onChange={(_, dat) => setDevId(`${dat.value}`)}
              style={{ marginRight: 10 }}
            />
            <Button primary onClick={onCreate}>
              Ajouter une Question
            </Button>
          </Container>
        </Container>
        {error && (
          <Message color="red">
            <MessageHeader>Une erreur est survenu</MessageHeader>
            <p>Veuillez rafraichir cette page</p>
          </Message>
        )}
        <Table celled padded>
          <TableHeader>
            <TableRow>
              <TableHeaderCell singleLine>ID Borne</TableHeaderCell>
              <TableHeaderCell singleLine>Question</TableHeaderCell>
              <TableHeaderCell singleLine>Date de Debut</TableHeaderCell>
              <TableHeaderCell singleLine>Date de Fin</TableHeaderCell>
              <TableHeaderCell singleLine>Date de Mise à Jour</TableHeaderCell>
              <TableHeaderCell singleLine>Réponses</TableHeaderCell>
              <TableHeaderCell singleLine>Actions</TableHeaderCell>
            </TableRow>
          </TableHeader>

          <DimmerDimmable as={TableBody} blurring>
            {questions
              .filter((e) => (devId.length > 0 ? e.dev_id === devId : true))
              .map((question) => {
                return (
                  <TableRow key={question.question_id}>
                    {columns.map((column, i) => (
                      <TableCell
                        key={`${question.question_id}-${i}`}
                        singleLine={
                          column === "date_debut" ||
                          column === "date_fin" ||
                          column === "data_count" ||
                          column === "date_mise_a_jour"
                        }
                      >
                        {column === "date_debut" ||
                        column === "date_fin" ||
                        column === "date_mise_a_jour"
                          ? DateTime.fromISO(question[column]).toFormat(
                              "y-MM-dd"
                            )
                          : question[column]}
                      </TableCell>
                    ))}
                    <TableCell singleLine>
                      <Button
                        circular
                        icon
                        as={Link}
                        to={`${question.question_id}`}
                        disabled={loading}
                      >
                        <Icon name="arrow right" />
                      </Button>

                      <Button
                        circular
                        icon
                        disabled={loading}
                        onClick={() => onDuplicate(question)}
                        title="Dupliquer"
                      >
                        <Icon name="copy outline" />
                      </Button>

                      {/* Ancienne version : Modification seulement avant le début de la question */}
                      {/* {DateTime.now().valueOf() <
                        DateTime.fromISO(question.date_debut).valueOf() && (
                        <Button
                          circular
                          icon
                          disabled={loading}
                          onClick={() => onEdit(question)}
                        >
                          <Icon name="edit outline" />
                        </Button>
                      )} */}
                      
                      {/* Nouvelle version : Modification tant que la question n'est pas terminée */}
                      {DateTime.now().valueOf() <
                        DateTime.fromISO(question.date_fin).valueOf() && (
                        <Button
                          circular
                          icon
                          disabled={loading}
                          onClick={() => onEdit(question)}
                        >
                          <Icon name="edit outline" />
                        </Button>
                      )}
                      {/* Suppression seulement avant le début de la question (règle conservée) */}
                      {DateTime.now().valueOf() <
                        DateTime.fromISO(question.date_debut).valueOf() && (
                        <Button
                          circular
                          icon
                          disabled={loading}
                          onClick={() => onDelete(question)}
                        >
                          <Icon name="trash alternate outline" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}

            {questions.length === 0 && !loading && (
              <TableRow style={{ height: "8rem" }}>
                <TableCell colSpan={columns.length + 1}>
                  <Segment placeholder>
                    <Header icon>
                      <Icon name="ban" />
                      Il n'y a aucune question enregistrée pour le moment
                    </Header>
                  </Segment>
                </TableCell>
              </TableRow>
            )}

            {loading && (
              <TableRow style={{ height: "8rem" }}>
                <TableCell colSpan={columns.length + 1}>
                  <Dimmer active inverted>
                    <Loader />
                  </Dimmer>
                </TableCell>
              </TableRow>
            )}
          </DimmerDimmable>
        </Table>
      </Container>

      <Modal open={open} onClose={() => dispatch({ type: "CLOSE_MODAL" })}>
        <ModalHeader>
          {isEdit ? "Éditer la question" : "Ajouter une question"}
        </ModalHeader>
        <Formik
          initialValues={{
            question: data.question,
            dev_id: data.dev_id,
            date_debut: data.date_debut,
            date_fin: data.date_fin,
          }}
          onSubmit={async (values) => {
            await submit(values);
          }}
          onReset={onClose}
          validationSchema={Yup.object().shape({
            question: Yup.string().required().min(5),
            dev_id: Yup.string().required(),
            date_debut: Yup.date()
              .transform((value) => new Date(value))
              .min(
                isEdit
                  ? null
                  : getTodayDate()
              )
              .max(Yup.ref("date_fin"))
              .required(),
            date_fin: Yup.date().required().min(Yup.ref("date_debut")),
          })}
        >
          {(props) => {
            const {
              values,
              touched,
              errors,
              isSubmitting,
              handleChange,
              handleReset,
              handleSubmit,
              setValues,
            } = props;
            return (
              <>
                <ModalContent>
                  {error && (
                    <Message color="red">
                      <MessageHeader>Une erreur s'est produite</MessageHeader>
                      <p>
                        Veuillez vérifier que les dates des différentes
                        questions ne se chevauchent pas et que les valeurs sont
                        correctes.
                      </p>
                    </Message>
                  )}
                  <Form>
                    <FormGroup widths="equal">
                      <DateInput
                        id="date_debut"
                        name="date_debut"
                        dateFormat="Y-MM-DD"
                        placeholder="Date de debut"
                        minDate={
                          isEdit
                            ? new Date(data.date_debut)
                            : getTodayDate()
                        }
                        maxDate={new Date(values.date_fin)}
                        value={values.date_debut}
                        iconPosition="left"
                        onChange={(e, { name, value }) => {
                          if (!values.date_fin.length) {
                            setValues({
                              ...values,
                              [name]: value,
                              date_fin: value,
                            });
                          } else {
                            setValues({ ...values, [name]: value });
                          }
                        }}
                      />
                      <DateInput
                        id="date_fin"
                        name="date_fin"
                        dateFormat="Y-MM-DD"
                        minDate={
                          isEdit
                            ? new Date(values.date_debut)
                            : getTodayDate()
                        }
                        placeholder="Date de fin"
                        value={values.date_fin}
                        iconPosition="left"
                        onChange={(_, { name, value }) =>
                          setValues({ ...values, [name]: value })
                        }
                        markColor="blue"
                      />
                    </FormGroup>
                    {errors.date_debut &&
                      errors.date_fin &&
                      touched.date_debut &&
                      touched.date_fin && (
                        <Message color="red">
                          <p>
                            Dates invalides ou potentiellement se chevauchant
                          </p>
                        </Message>
                      )}

                    <Select
                      placeholder="Selectionner une borne"
                      options={devices}
                      disabled={isEdit}
                      selection
                      error={errors.dev_id && touched.dev_id ? true : false}
                      onChange={(_, dat) =>
                        setValues({ ...values, dev_id: `${dat.value}` })
                      }
                      value={values.dev_id}
                      style={{ width: "100%", margin: "10px 0" }}
                    />

                    <FormTextArea
                      id="question"
                      name="question"
                      label="Question"
                      placeholder="Question..."
                      value={values.question}
                      onChange={handleChange}
                      error={
                        errors.question && touched.question
                          ? {
                              content: "La question est requise",
                              pointing: "below",
                            }
                          : false
                      }
                    />
                  </Form>
                </ModalContent>
                <ModalActions>
                  <Button onClick={handleReset}>Fermer</Button>
                  <Button
                    primary
                    type="button"
                    onClick={(e) => handleSubmit()}
                    disabled={isSubmitting}
                    loading={isSubmitting}
                  >
                    {isEdit ? "Sauvegarder" : "Creer"}
                  </Button>
                </ModalActions>
              </>
            );
          }}
        </Formik>
      </Modal>

      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        dimmer
      >
        <ModalHeader>Supprimer la question</ModalHeader>
        <ModalContent>
          {errorDelete && (
            <Message color="red">
              <MessageHeader>Une erreur s'est produite</MessageHeader>
              <p>Impossible de supprimer la question.</p>
            </Message>
          )}
          <p>Êtes-vous sûr de vouloir supprimer cette question</p>
        </ModalContent>
        <ModalActions>
          <Button onClick={() => setDeleteModalOpen(false)} negative>
            Non
          </Button>
          <Button onClick={submitDelete} positive loading={loading}>
            Oui
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
};
export default QuestionsPage;
